import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { roleHome } from '../utils/roleHome';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Decode JWT token to get expiry time
 * @param {string} token - JWT token string
 * @returns {number|null} - Expiry timestamp in ms, or null if invalid
 */
const getTokenExpiry = (token) => {
  if (!token) return null;
  try {
    // JWT structure: header.payload.signature
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

// Time before expiry to refresh token (10 minutes)
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token and user from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        // Check if token is expired
        const expiry = getTokenExpiry(savedToken);
        if (expiry && expiry < Date.now()) {
          // Token already expired, clear it
          console.log('[AUTH] Stored token expired, clearing');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } else {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
        }
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (usernameOrToken, passwordOrUser) => {
    // Check if this is a direct token+user login (for activation flow)
    // If the second parameter is an object (not a string), it's a user object
    if (typeof passwordOrUser === 'object' && passwordOrUser !== null) {
      // This is a direct token+user login
      const tokenStr = usernameOrToken;
      const userObj = passwordOrUser;

      localStorage.setItem('token', tokenStr);
      localStorage.setItem('user', JSON.stringify(userObj));
      setToken(tokenStr);
      setUser(userObj);

      return { success: true };
    }

    // Regular username/password login
    try {
      const response = await authAPI.login({ username: usernameOrToken, password: passwordOrUser });
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(access_token);
      setUser(user);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  // Auto-refresh token before it expires
  useEffect(() => {
    if (!token) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) {
      console.log('[AUTH] Could not decode token expiry');
      return;
    }

    // Calculate when to refresh (10 min before expiry)
    const refreshAt = expiry - TOKEN_REFRESH_BUFFER_MS;
    const timeUntilRefresh = refreshAt - Date.now();

    // If token expires in less than buffer time, refresh immediately
    if (timeUntilRefresh <= 0) {
      console.log('[AUTH] Token expiring soon, refreshing now');
      authAPI.refreshToken()
        .then(response => {
          const newToken = response.data.access_token;
          localStorage.setItem('token', newToken);
          setToken(newToken);
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setUser(response.data.user);
          }
          console.log('[AUTH] Token refreshed successfully');
        })
        .catch(error => {
          console.error('[AUTH] Token refresh failed:', error);
          // If refresh fails, logout the user
          logout();
        });
      return;
    }

    // Schedule refresh
    console.log(`[AUTH] Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);

    const timeoutId = setTimeout(async () => {
      try {
        console.log('[AUTH] Refreshing token...');
        const response = await authAPI.refreshToken();
        const newToken = response.data.access_token;

        localStorage.setItem('token', newToken);
        setToken(newToken);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setUser(response.data.user);
        }

        console.log('[AUTH] Token refreshed successfully');
      } catch (error) {
        console.error('[AUTH] Token refresh failed:', error);
        // If refresh fails, logout the user
        logout();
      }
    }, timeUntilRefresh);

    return () => clearTimeout(timeoutId);
  }, [token, logout]);

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  /**
   * Ansichtsmodus starten: als Admin die App mit den Augen eines anderen Users
   * sehen. Serverseitig ist die Sitzung schreibgeschützt. Danach wird die Seite
   * hart neu geladen, damit keine Daten der Admin-Sitzung (Board-Caches, Chat,
   * Sockets) in den Ansichtsmodus lecken.
   */
  const impersonate = async (userId) => {
    try {
      const res = await authAPI.impersonate(userId);
      const { access_token, user: target } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(target));
      window.location.assign(roleHome(target));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Ansichtsmodus fehlgeschlagen' };
    }
  };

  /** Zurück ins eigene Admin-Konto. */
  const stopImpersonation = async () => {
    try {
      const res = await authAPI.stopImpersonation();
      const { access_token, user: admin } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(admin));
      window.location.assign(roleHome(admin));
      return { success: true };
    } catch (error) {
      // Im Zweifel lieber ausloggen als im fremden Konto hängen bleiben
      logout();
      window.location.assign('/login');
      return { success: false, error: error.response?.data?.error || 'Wechsel fehlgeschlagen' };
    }
  };

  const isImpersonating = () => !!(user && user.impersonated_by);

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isClient = () => {
    return user && user.role === 'client';
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAdmin,
    isClient,
    impersonate,
    stopImpersonation,
    isImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
